import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEndpoints } from "../../../hooks/useEndpoints";
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import GenericModalV2 from "../../../shared/GenericModal/GenericModal";
import { CustomFormV3 } from "../../../shared/CustomFormV3/CustomForm";
import { FieldDef } from "../../../shared/CustomFormV3/types";
import { applyServerErrors } from "../../../shared/CustomFormV3/serverErrors";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { EVENT_TYPES } from "../../../enums/event";
import { IEvent } from "../../../models/event";
import { IWorkout } from "../../../models/programming";
import { Paginated } from "../../../typings/Paginated";
import { ASSIGNMENT_TYPE, ASSIGNMENT_ORIGIN, DAY_MAPPING } from "../../../enums/programming";
import CustomTabsV3 from "../../../shared/CustomTabsV3/CustomTabsV3";
import Typhography from "../../../shared/Typhography/Typhography";
import MultiDateInlinePicker from "../../../shared/MultiDateInlinePicker/MultiDateInlinePicker";
import { Tabs, Spinner } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { Plus } from "@phosphor-icons/react";
import styles from "./AddEventModal.module.scss";

type WorkoutSubType = "campo" | "scheda" | null;

export type IEVentForm = {
  type: any;
  description: string;
  location: string;
  testType: any;
  name: string;
  teams: any[];
  startDate: Date | null;
  endDate?: Date | null;
  date: Date | null;
  users: any[];
  isRecurring: boolean;
  rrule: string | null;
};

const tabs = [
  { label: "Informazioni Evento", value: "info" },
  { label: "Invitati", value: "invited" },
];

const types = [
  { label: "Allenamento", value: EVENT_TYPES.WORKOUT },
  { label: "Test", value: EVENT_TYPES.TEST },
  { label: "Gara", value: EVENT_TYPES.MATCH },
];

const BASE_TEST_OPTIONS = [
  { label: "Drop Jump Test", value: "drop-jump", isSystemTest: false },
  { label: "Cmj", value: "cmj", isSystemTest: false },
];

const isTestType = Joi.alternatives([
  Joi.string().valid(EVENT_TYPES.TEST),
  Joi.object({ value: Joi.string().valid(EVENT_TYPES.TEST) }).unknown(),
]);

const eventSchema = Joi.object({
  type: Joi.any().required(),
  name: Joi.when("type", {
    is: isTestType,
    then: Joi.string().allow("", null).optional(),
    otherwise: Joi.string().trim().min(1).required().messages({
      "string.empty": "Il nome è obbligatorio",
      "string.min": "Il nome è obbligatorio",
      "any.required": "Il nome è obbligatorio",
    }),
  }),
  testType: Joi.any()
    .allow(null)
    .when("type", {
      is: isTestType,
      then: Joi.required().messages({ "any.required": "Campo obbligatorio" }),
      otherwise: Joi.optional(),
    }),
  date: Joi.date().required().messages({
    "date.base": "Campo obbligatorio",
    "any.required": "Campo obbligatorio",
  }),
  startDate: Joi.any()
    .custom((val, helpers) => {
      const parent = helpers.state.ancestors[0];
      const isTest = isTestType.validate(parent?.type).error === undefined;
      if (!isTest && !val) return helpers.error("any.required");
      if (parent?.endDate && !val) return helpers.error("any.required");
      return val;
    })
    .messages({ "any.required": "Campo obbligatorio" }),
  endDate: Joi.any()
    .custom((val, helpers) => {
      const parent = helpers.state.ancestors[0];
      if (parent?.startDate && !val) return helpers.error("any.required");
      return val;
    })
    .messages({ "any.required": "Campo obbligatorio" }),
  location: Joi.string().allow("", null).optional(),
  description: Joi.string().allow("", null).optional(),
  teams: Joi.array()
    .custom((val, helpers) => {
      const parent = helpers.state.ancestors[0];
      if (
        (!val || val.length === 0) &&
        (!parent?.users || parent.users.length === 0)
      ) {
        return helpers.error("array.atLeastOne");
      }
      return val;
    })
    .messages({ "array.atLeastOne": "Aggiungi almeno un team o un utente" }),
  users: Joi.array().optional(),
  isRecurring: Joi.boolean().optional(),
  rrule: Joi.string().allow(null).optional(),
}).options({ allowUnknown: true });

const initialValue: IEVentForm = {
  type: EVENT_TYPES.WORKOUT,
  description: "",
  startDate: null,
  endDate: null,
  date: null,
  location: "",
  testType: null,
  name: "",
  teams: [],
  users: [],
  isRecurring: false,
  rrule: null,
};

export const AddEventModal: FC<{
  isOpen?: IEvent | string;
  onClose: () => void;
  onConfirm: (form: any) => void | Promise<void>;
  isInvalidForm: (form: any) => boolean;
  isLoading?: boolean;
  recurringScope?: "this" | "this_and_following" | "all" | null;
  onCreateNewWorkout?: (date: Date | null) => void;
  /** Team precompilati in CREATE (es. la squadra del dettaglio team). */
  presetTeams?: any[];
}> = ({
  isOpen,
  isLoading,
  onConfirm,
  onClose,
  isInvalidForm,
  recurringScope,
  onCreateNewWorkout,
  presetTeams,
}) => {
  const [currentTab, setCurrentTab] = useState(tabs[0].value);
  const [workoutSubType, setWorkoutSubType] = useState<WorkoutSubType>(null);
  const [selectedWorkoutId, setSelectedWorkoutId] = useState<string | null>(null);
  const [workoutSearch, setWorkoutSearch] = useState("");
  /**
   * Date multiple (giorni discreti) per Test, Allenamento "campo" e Gara in
   * CREATE non ricorrente: il caller le espande in N eventi (uno per data).
   * Fuori da questa modalità resta in uso il campo single "date" del form
   * (edit, ricorrenza via rrule, Allenamento "scheda").
   */
  const [eventDates, setEventDates] = useState<string[]>([]);

  const queryClient = useQueryClient();
  const endpoints = useEndpoints();

  const methods = useForm<IEVentForm>({
    values: initialValue,
    resolver: joiResolver(eventSchema),
  });
  const {
    watch,
    reset,
    setValue,
    formState: { errors },
  } = methods;

  const INFO_KEYS = new Set([
    "type",
    "name",
    "testType",
    "date",
    "startDate",
    "endDate",
    "location",
    "description",
    "isRecurring",
    "rrule",
  ]);
  const INVITED_KEYS = new Set(["users", "teams"]);
  const infoHasError = Object.keys(errors).some((k) => INFO_KEYS.has(k));
  const invitedHasError = Object.keys(errors).some((k) => INVITED_KEYS.has(k));

  const watchedTeams = watch("teams");
  const watchedType = watch("type");
  const watchedIsRecurring = watch("isRecurring");
  const currentType = (watchedType as any)?.value ?? watchedType;
  const isWorkoutType = currentType === EVENT_TYPES.WORKOUT;
  const isTestType = currentType === EVENT_TYPES.TEST;
  const isMatchType = currentType === EVENT_TYPES.MATCH;
  const isEditing = !!isOpen && typeof isOpen !== "string";
  const isSchedaMode = workoutSubType === "scheda";
  // Allenamento con selezione di giorni discreti = solo sotto-tipo "campo"
  // (la "scheda" ha un path separato ad assignment con la sua data singola).
  const isCampoWorkout = isWorkoutType && workoutSubType === "campo";
  // Multi-data disponibile per Test, Allenamento "campo" e Gara, solo in CREATE
  // non ricorrente. Esclusi: edit (multi-data è create-only, il caller espande
  // in N POST solo in creazione) e ricorrenza (alternativa via rrule).
  const showMultiDatePicker =
    !isEditing &&
    !watchedIsRecurring &&
    (isTestType || isCampoWorkout || isMatchType);

  // Reset del sotto-tipo Allenamento quando l'evento non è più un Allenamento.
  useEffect(() => {
    if (!isWorkoutType) {
      setWorkoutSubType(null);
      setSelectedWorkoutId(null);
      setWorkoutSearch("");
    }
  }, [isWorkoutType]);

  // Multi-data: svuota le date fuori dalla modalità multi-data (edit, ricorrenza,
  // scheda); pre-seleziona oggi entrando in modalità su nuovo evento (lista vuota).
  useEffect(() => {
    if (!showMultiDatePicker) {
      setEventDates([]);
      return;
    }
    setEventDates((prev) =>
      prev.length > 0 ? prev : [dayjs().format("YYYY-MM-DD")],
    );
  }, [showMultiDatePicker]);

  // Sync eventDates[0] → form.date so Joi `date.required` passes and
  // handleSubmit can fire. Without this, the form silently fails validation
  // because `date` stays null while only the local `eventDates` state holds
  // the user's selection (Bug 994f4a, 2026-05-14).
  useEffect(() => {
    if (showMultiDatePicker && eventDates.length > 0) {
      setValue("date", new Date(eventDates[0] + "T12:00:00"), {
        shouldValidate: true,
      });
    }
  }, [showMultiDatePicker, eventDates, setValue]);

  const catalogQuery = useQuery({
    queryKey: ["testing-catalog"],
    queryFn: async () => {
      const { data } = await endpoints.getTestCatalog();
      return data;
    },
    staleTime: 1000 * 60 * 30,
  });

  const testTypeOptions = useMemo(() => {
    const catalog: any[] = catalogQuery.data ?? [];
    const teams: any[] = Array.isArray(watchedTeams) ? watchedTeams : [];
    const enabledIds = new Set<string>();
    teams.forEach((t: any) => {
      const tests: any[] = t.enabledTests ?? [];
      tests.forEach((id: any) => {
        const resolved = typeof id === "string" ? id : id._id;
        if (resolved) enabledIds.add(resolved);
      });
    });

    const teamTestOptions = catalog
      .filter((t: any) => enabledIds.has(t._id) || enabledIds.has(t.id))
      .map((t: any) => ({ label: t.name, value: t._id, isSystemTest: true }));

    const baseValues = new Set(BASE_TEST_OPTIONS.map((o) => o.value));
    const extra = teamTestOptions.filter((o) => !baseValues.has(o.value));

    return [...BASE_TEST_OPTIONS, ...extra];
  }, [watchedTeams, catalogQuery.data]);

  // Workout templates query
  const workoutTemplatesQuery = useQuery({
    queryKey: ["workout-templates-for-event"],
    queryFn: async () => {
      const { data } = await endpoints.getProgrammingWorkouts({
        isTemplate: true,
        limit: 100,
      });
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: isSchedaMode,
  });

  const workoutTemplates: IWorkout[] = useMemo(
    () => (workoutTemplatesQuery.data as Paginated<IWorkout> | undefined)?.docs ?? [],
    [workoutTemplatesQuery.data]
  );

  const filteredWorkouts = useMemo(() => {
    if (!workoutSearch.trim()) return workoutTemplates;
    const q = workoutSearch.toLowerCase();
    return workoutTemplates.filter((w) => w.title.toLowerCase().includes(q));
  }, [workoutTemplates, workoutSearch]);

  // Mutation: create assignment from calendar
  const createAssignmentMutation = useMutation({
    mutationFn: async (params: {
      workoutId: string;
      date: string;
      assignees: any[];
    }) => {
      const { data } = await endpoints.postProgrammingAssignment({
        workoutId: params.workoutId,
        assignmentType: ASSIGNMENT_TYPE.SCHEDULED,
        assignees: params.assignees,
        scheduledDates: [params.date],
        dayMapping: DAY_MAPPING.CONSECUTIVE,
        createdFrom: ASSIGNMENT_ORIGIN.CALENDAR_GLOBAL,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programming-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      onClose();
    },
  });

  const handleCreateWorkoutEvent = useCallback(() => {
    if (!selectedWorkoutId) return;
    const formData = watch();
    const date = formData.date
      ? dayjs(formData.date).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    const assignees = [
      ...(formData.teams || []).map((t: any) => ({
        type: "team" as const,
        id: t._id || t,
      })),
      ...(formData.users || []).map((u: any) => ({
        type: "athlete" as const,
        id: u._id || u,
      })),
    ];
    if (assignees.length === 0) return;
    createAssignmentMutation.mutate({ workoutId: selectedWorkoutId, date, assignees });
  }, [selectedWorkoutId, watch, createAssignmentMutation]);

  const handleWorkoutSubTypeChange = useCallback(
    (subType: WorkoutSubType) => {
      setWorkoutSubType(subType);
      setSelectedWorkoutId(null);
      setWorkoutSearch("");
    },
    []
  );

  const formValues = watch();
  const normalizedForm = {
    ...formValues,
    type: (formValues.type as any)?.value ?? formValues.type,
    testType: (formValues.testType as any)?.value ?? formValues.testType,
    // In modalità multi-data la `date` viene mappata da eventDates al submit;
    // passo le date al validator così che `!form.date` non blocchi il bottone
    // quando l'utente ha selezionato giorni nel MultiDateInlinePicker.
    date:
      showMultiDatePicker && eventDates.length > 0
        ? new Date(eventDates[0] + "T12:00:00")
        : formValues.date,
    dates: showMultiDatePicker ? eventDates : undefined,
  };
  const isInvalid = isInvalidForm(normalizedForm);

  useEffect(() => {
    if (isOpen && typeof isOpen !== "string") {
      const event: any = { ...isOpen };
      const isRecurringEvent = !!(
        event.recurringEventId || event.recurrence?.rrule
      );
      const isSingleScope = recurringScope === "this";
      const isSystemTest = event.type === EVENT_TYPES.SYSTEM_TEST_EVENT;
      const resolvedTestType = isSystemTest
        ? typeof event.testId === "object"
          ? event.testId?._id
          : event.testId
        : (event.testType ?? null);

      reset({
        type: isSystemTest ? EVENT_TYPES.TEST : event.type,
        description: event.description,
        startDate: event.startDate ? dayjs(event.startDate).toDate() : null,
        endDate: event.endDate ? dayjs(event.endDate).toDate() : null,
        // Alternativa-A (eventi con orari): event.date può essere null/undefined;
        // si usa event.startDate come fallback. Se entrambi assenti non si
        // defaulta a oggi (dayjs(undefined) → Invalid Date → oggi): si lascia null.
        date: (event.date || event.startDate) ? dayjs(event.date ?? event.startDate).toDate() : null,
        location: event.location,
        testType: resolvedTestType,
        name: event.name,
        teams: event.teams,
        users: event.users,
        isRecurring: isRecurringEvent && !isSingleScope,
        rrule: !isSingleScope ? (event.recurrence?.rrule ?? null) : null,
      });
    } else if (isOpen) {
      reset({
        ...initialValue,
        date: dayjs(isOpen).toDate(),
        teams: presetTeams ?? [],
      });
      setWorkoutSubType(null);
      setSelectedWorkoutId(null);
      setWorkoutSearch("");
    } else {
      setCurrentTab(tabs[0].value);
      reset(initialValue);
      setWorkoutSubType(null);
      setSelectedWorkoutId(null);
      setWorkoutSearch("");
    }
  }, [isOpen, reset, recurringScope, presetTeams]);

  const onSubmit = async (data: IEVentForm) => {
    const rawTestType = (data.testType as any)?.value ?? data.testType;
    const isSystemTest =
      !!rawTestType && rawTestType !== "drop-jump" && rawTestType !== "cmj";
    const resolvedType = (data.type as any)?.value ?? data.type;

    const body: any = {
      ...data,
      type: resolvedType,
    };

    // Multi-data (Test, Allenamento "campo", Gara in create non ricorrente):
    // popola l'array body.dates che il caller espande in N postEvent paralleli.
    // Per back-compat, body.date resta valorizzato alla prima data.
    if (showMultiDatePicker && eventDates.length > 0) {
      const sorted = [...eventDates].sort();
      body.dates = sorted;
      body.date = new Date(sorted[0] + "T12:00:00");
    }

    if (body.type === EVENT_TYPES.TEST && isSystemTest) {
      body.type = EVENT_TYPES.SYSTEM_TEST_EVENT;
      body.testId = rawTestType;
      delete body.testType;
    } else {
      body.testType = rawTestType;
    }

    if (data.isRecurring && data.rrule) {
      body.recurrence = { rrule: data.rrule, exdates: [] };
    }
    delete body.isRecurring;
    delete body.rrule;
    try {
      await onConfirm(body);
    } catch (error: any) {
      // 400 di validazione: errori sotto ai campi via RHF
      if (applyServerErrors(error, methods.setError)) {
        toast.error("Controlla i campi evidenziati");
        return;
      }
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : "Qualcosa è andato storto"
      );
    }
  };

  // Hide name/time/location/description/recurrence when in "scheda" mode
  const isFieldHiddenByScheda = (key: string) =>
    isSchedaMode && ["name", "startDate", "endDate", "location", "description", "isRecurring", "rrule"].includes(key);

  const infoLayout: FieldDef[] = [
    {
      key: "type",
      type: "single-select",
      size: 12,
      options: types,
      disabled: isEditing,
      label: "Tipologia evento",
    },
    {
      key: "name",
      type: "text-input",
      placeholder: "Inserisci il nome",
      size: 12,
      visibleIf: ({ type }) =>
        ((type as any)?.value ?? type) !== EVENT_TYPES.TEST &&
        !isFieldHiddenByScheda("name"),
      deps: ["type"],
      label: "Titolo *",
    },
    {
      key: "testType",
      type: "single-select",
      options: testTypeOptions,
      visibleIf: ({ type }) =>
        ((type as any)?.value ?? type) === EVENT_TYPES.TEST,
      deps: ["type"],
      placeholder: "Seleziona tipo test",
      size: 12,
      label: "Tipologia test *",
    },
    // Campo "Data" singolo omesso interamente quando il MultiDateInlinePicker è
    // attivo (Test/Allenamento campo/Gara in create non ricorrente): altrimenti
    // il GridItem occuperebbe colSpan anche se il contenuto è null, sballando la
    // larghezza di Dalle/Alle ore. Fuori da quel caso (edit, ricorrenza, scheda,
    // Allenamento senza sotto-tipo) il campo è SEMPRE mostrato: è il fallback a
    // data singola, la scelta di quando ometterlo è già in showMultiDatePicker.
    ...(showMultiDatePicker
      ? []
      : [
          {
            key: "date",
            type: "date" as const,
            size: isSchedaMode ? 12 : 4,
            label: "Data *",
          },
        ]),
    {
      key: "startDate",
      type: "date",
      size: showMultiDatePicker ? 6 : 4,
      label: "Dalle ore",
      placeholder: "HH:MM",
      isClearable: true,
      showTimeSelectOnly: true,
      showTimeSelect: true,
      showIcon: false,
      dateFormat: "HH:mm",
      timeIntervals: 5,
      timeCaption: "Inizio",
      visibleIf: () => !isFieldHiddenByScheda("startDate"),
    },
    {
      key: "endDate",
      type: "date",
      size: showMultiDatePicker ? 6 : 4,
      label: "Alle ore",
      placeholder: "HH:MM",
      isClearable: true,
      showTimeSelectOnly: true,
      showTimeSelect: true,
      showIcon: false,
      dateFormat: "HH:mm",
      timeIntervals: 5,
      timeCaption: "Fine",
      deps: ["startDate"],
      maxTime: ({ startDate }) =>
        startDate ? dayjs(startDate).endOf("day").toDate() : undefined,
      minTime: ({ startDate }) =>
        startDate ? dayjs(startDate).toDate() : undefined,
      visibleIf: () => !isFieldHiddenByScheda("endDate"),
    },
    {
      key: "location",
      type: "text-input",
      placeholder: "Inserisci posizione attività",
      size: 12,
      label: "Posizione Attività",
      visibleIf: () => !isFieldHiddenByScheda("location"),
    },
    {
      key: "description",
      type: "text-area",
      placeholder: "Inserisci una descrizione...",
      size: 12,
      label: "Descrizione",
      visibleIf: () => !isFieldHiddenByScheda("description"),
    },
    {
      key: "isRecurring",
      type: "boolean",
      label: "Evento periodico",
      size: 12,
      visibleIf: () => !isEditing && !isFieldHiddenByScheda("isRecurring"),
    },
    {
      key: "rrule",
      type: "rrule-builder",
      label: "Ricorrenza",
      size: 12,
      visibleIf: ({ isRecurring }) =>
        !isFieldHiddenByScheda("rrule") &&
        !!isRecurring &&
        (!isEditing || recurringScope === "all"),
      deps: ["isRecurring"],
    },
  ];

  const isSubmitDisabled = isSchedaMode
    ? !selectedWorkoutId || createAssignmentMutation.isPending
    : showMultiDatePicker && eventDates.length === 0;

  return (
    <FormProvider {...methods}>
      <GenericModalV2
        title={isEditing ? "Modifica Evento" : "Nuovo Evento"}
        size={showMultiDatePicker ? "xl" : "lg"}
        isOpen={!!isOpen}
        isLoading={isLoading}
        onClose={onClose}
        Footer={
          <>
            <CustomButton
              color="outline"
              onClick={onClose}
              disabled={isLoading || createAssignmentMutation.isPending}
            >
              Annulla
            </CustomButton>
            <CustomButton
              color="secondary"
              onClick={
                isSchedaMode && selectedWorkoutId
                  ? handleCreateWorkoutEvent
                  : methods.handleSubmit(onSubmit)
              }
              isLoading={isLoading || createAssignmentMutation.isPending}
              disabled={isSubmitDisabled}
            >
              Conferma
            </CustomButton>
          </>
        }
      >
        <CustomTabsV3
          value={currentTab}
          tabs={[
            { ...tabs[0], hasError: infoHasError },
            { ...tabs[1], hasError: invitedHasError },
          ]}
          onValueChange={(e) => setCurrentTab(e.value)}
        >
          <Tabs.Content value="info">
            <div className={showMultiDatePicker ? styles.splitLayout : "mt-3"}>
              <div className={showMultiDatePicker ? styles.formSide : ""}>
                <CustomFormV3 layout={infoLayout} />

              {/* Contextual type info banner */}
              {(() => {
                const typeInfoMap: Record<string, string> = {
                  [EVENT_TYPES.WORKOUT]: "Scegli se registrare una seduta campo o creare una scheda strutturata",
                  [EVENT_TYPES.TEST]: "Per prescrivere test di readiness (CMJ, Drop Jump)",
                  [EVENT_TYPES.MATCH]: "Per le partite. Raccoglie RPE post-gara.",
                };
                const msg = typeInfoMap[currentType];
                if (!msg) return null;
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 7,
                      marginTop: 6,
                      marginBottom: 2,
                      padding: "7px 10px",
                      borderRadius: 8,
                      background: "rgba(6, 52, 68, 0.04)",
                      border: "1px solid rgba(6, 52, 68, 0.08)",
                    }}
                  >
                    <Info size={13} style={{ color: "#6b8a97", flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 12, color: "#6b8a97", lineHeight: 1.4 }}>{msg}</span>
                  </div>
                );
              })()}

              {/* Workout sub-type: campo vs scheda */}
              {isWorkoutType && !isEditing && (
                <div style={{
                  marginTop: 20,
                  padding: "16px",
                  borderRadius: 12,
                  background: "#f6f6f6",
                  border: "1px solid #e7e7e7",
                }}>
                  <div style={{ marginBottom: 14 }}>
                    <Typhography size="bold-12">
                      Tipo di allenamento
                    </Typhography>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {(
                      [
                        {
                          value: "campo",
                          label: "Allenamento campo",
                          description: "Sessione classica con orario e luogo",
                        },
                        {
                          value: "scheda",
                          label: "Scheda allenamento",
                          description: "Scheda strutturata con il builder",
                        },
                      ] as const
                    ).map((opt) => (
                      <div
                        key={opt.value}
                        onClick={() => handleWorkoutSubTypeChange(opt.value)}
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          padding: "12px 14px",
                          borderRadius: 10,
                          border:
                            workoutSubType === opt.value
                              ? "1.5px solid #00212c"
                              : "1px solid #e7e7e7",
                          background:
                            workoutSubType === opt.value
                              ? "#fff"
                              : "#fff",
                          boxShadow:
                            workoutSubType === opt.value
                              ? "0 1px 4px rgba(0, 0, 0, 0.08)"
                              : "none",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border:
                              workoutSubType === opt.value
                                ? "2px solid #00212c"
                                : "2px solid #e7e7e7",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        >
                          {workoutSubType === opt.value && (
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#00212c",
                              }}
                            />
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Typhography size="medium-12">
                            {opt.label}
                          </Typhography>
                          <Typhography size="regular-12" className="color-text-2">
                            {opt.description}
                          </Typhography>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Workout template selector */}
              {isSchedaMode && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 16,
                    borderRadius: 12,
                    background: "#f6f6f6",
                    border: "1px solid #e7e7e7",
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <Typhography size="bold-12">
                      Seleziona dalla libreria
                    </Typhography>
                    <div
                      onClick={() => {
                        const formData = watch();
                        onCreateNewWorkout?.(formData.date);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#063444',
                        transition: 'opacity 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                    >
                      <Plus size={12} weight="bold" />
                      <span>Crea nuova</span>
                    </div>
                  </div>

                  <input
                    type="text"
                    placeholder="Cerca scheda..."
                    value={workoutSearch}
                    onChange={(e) => setWorkoutSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: "1px solid #e7e7e7",
                      fontFamily: "'Satoshi', sans-serif",
                      fontSize: 13,
                      color: "#00212c",
                      background: "#fff",
                      outline: "none",
                      marginBottom: 10,
                      boxSizing: "border-box",
                    }}
                  />

                  {workoutTemplatesQuery.isLoading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 20,
                      }}
                    >
                      <Spinner size="sm" />
                    </div>
                  ) : filteredWorkouts.length === 0 ? (
                    <Typhography
                      size="regular-12"
                      style={{
                        textAlign: "center",
                        padding: 16,
                        color: "#b5b5b5",
                      }}
                    >
                      {workoutSearch
                        ? "Nessuna scheda trovata"
                        : "Nessuna scheda template. Creane una dalla libreria."}
                    </Typhography>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        maxHeight: 240,
                        overflowY: "auto",
                      }}
                    >
                      {filteredWorkouts.map((wt) => {
                        const isSelected = selectedWorkoutId === wt._id;
                        return (
                          <div
                            key={wt._id}
                            onClick={() => setSelectedWorkoutId(wt._id)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "10px 14px",
                              borderRadius: 10,
                              border: isSelected
                                ? "1.5px solid #00212c"
                                : "1px solid #e7e7e7",
                              background: isSelected
                                ? "rgba(6, 52, 68, 0.03)"
                                : "#fff",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                          >
                            <div
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                border: isSelected
                                  ? "2px solid #00212c"
                                  : "2px solid #e7e7e7",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {isSelected && (
                                <div
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: "#00212c",
                                  }}
                                />
                              )}
                            </div>
                            <div style={{ flex: 1 }}>
                              <Typhography size="bold-14">
                                {wt.title}
                              </Typhography>
                              <Typhography
                                size="regular-12"
                                className="color-text-2"
                              >
                                {wt.days.length} giorn
                                {wt.days.length === 1 ? "o" : "i"}
                                {wt.estimatedDuration
                                  ? ` · ~${wt.estimatedDuration} min`
                                  : ""}
                                {wt.tags.length > 0
                                  ? ` · ${wt.tags.slice(0, 2).join(", ")}`
                                  : ""}
                              </Typhography>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              </div>

              {/* Multi-date picker — Test, Allenamento "campo" e Gara in create
                  non ricorrente. Con ricorrenza o in edit si torna al field
                  "date" + rrule. Sostituisce il field "date" single nascosto
                  via visibleIf. */}
              {showMultiDatePicker && (
                <aside className={styles.pickerSide}>
                  <MultiDateInlinePicker
                    value={eventDates}
                    onChange={setEventDates}
                    label={
                      isTestType
                        ? "Date test *"
                        : isMatchType
                        ? "Date gara *"
                        : "Date allenamento *"
                    }
                    emptyHint="Clicca sul calendario per selezionare uno o più giorni"
                  />
                </aside>
              )}
            </div>
          </Tabs.Content>
          <Tabs.Content value="invited">
            <div className="mt-3">
              <CustomFormV3
                layout={[
                  { key: "users", type: "select-users", size: 12 },
                  { key: "teams", type: "select-teams", size: 12 },
                ]}
              />
            </div>
          </Tabs.Content>
        </CustomTabsV3>
      </GenericModalV2>
    </FormProvider>
  );
};

export default AddEventModal;
